import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, GraduationCap, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = ({ onLogin, onToggleLanguage }) => {
  const { t, i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'student'  // Always student (trainee) by default, admin can change later
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, role: 'student' };  // Force student role on registration

      const response = await axios.post(`${API}${endpoint}`, payload);
      const { access_token, user } = response.data;

      toast.success(isLogin ? t('welcomeBack') : t('success'));
      onLogin(access_token, user);
    } catch (error) {
      const message = error.response?.data?.detail || t('error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <Button
          data-testid="language-toggle"
          onClick={onToggleLanguage}
          variant="outline"
          size="icon"
          className="rounded-full"
        >
          <Globe className="h-5 w-5" />
        </Button>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden md:flex flex-col items-center justify-center space-y-6 p-8">
          <div className="relative">
            <img 
              src="/logo.png" 
              alt={t('appName')} 
              className="h-32 w-32 object-contain"
            />
          </div>
          <h1 className="text-5xl font-bold text-center">
            <span className="bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent">
              {t('appFullName')}
            </span>
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-md">
            {i18n.language === 'ar'
              ? 'بنك أسئلة شامل'
              : 'Comprehensive Question Bank'}
          </p>
          <div className="flex gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600">5000+</div>
              <div className="text-sm text-gray-600">{t('totalQuestions')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-600">+470</div>
              <div className="text-sm text-gray-600">{t('student')}</div>
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form */}
        <Card className="shadow-2xl border-0" data-testid="auth-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center">
              {isLogin ? t('login') : t('register')}
            </CardTitle>
            <CardDescription className="text-center">
              {isLogin ? t('welcomeBack') : t('createAccount')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">{t('name')}</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="name-input"
                    type="text"
                    placeholder={t('name')}
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="h-12"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  name="email"
                  data-testid="email-input"
                  type="email"
                  placeholder={t('email')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  name="password"
                  data-testid="password-input"
                  type="password"
                  placeholder={t('password')}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>

              <Button
                data-testid="submit-button"
                type="submit"
                className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('loading')}
                  </div>
                ) : isLogin ? (
                  t('login')
                ) : (
                  t('register')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                data-testid="toggle-auth-mode"
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: '', password: '', name: '', role: 'student' });
                }}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                {isLogin ? t('dontHaveAccount') : t('alreadyHaveAccount')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;